import React, { useState, useEffect } from 'react';
import './App.css';
import placementData from './data/placementData';
import editHistory from './data/editHistory';
import StatsCard from './components/StatsCard';
import Odometer from './components/Odometer';
import FilterControls from './components/FilterControls';
import PlacementTable from './components/PlacementTable';
import SuggestionBox from './components/SuggestionBox';
import InfoBanner from './components/InfoBanner';
import ChangeLog from './components/ChangeLog';
import { initGA, logPageView, logSearch, logFilterChange, logSortChange, logStatsToggle } from './utils/analytics';


function App() {
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campusFilter, setCampusFilter] = useState('all');
  const [sortType, setSortType] = useState('ctc-high');
  const [stats, setStats] = useState({});
  const [showStats, setShowStats] = useState(true);

  useEffect(() => {
    // Initialize Google Analytics from environment variable
    const gaId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (gaId) {
      initGA(gaId);
      logPageView();
    }

    // Calculate stats
    const calculatedStats = calculateStats();
    setStats(calculatedStats);
    
    // Initial sort by CTC high to low
    const sorted = [...placementData].sort((a, b) => b.ctc - a.ctc);
    setFilteredData(sorted);
  }, []);

  useEffect(() => {
    filterAndSort();
    // eslint-disable-next-line
  }, [searchTerm, statusFilter, campusFilter, sortType]);

  const calculateStats = () => {
    // Calculate average CTC (weighted by number of students placed)
    const placedWithCtc = placementData.filter(
      (item) => item.ctc > 0 && item.placed > 0
    );
    const totalCtcWeighted = placedWithCtc.reduce(
      (sum, item) => sum + item.ctc * item.placed,
      0
    );
    const totalStudentsPlaced = placedWithCtc.reduce(
      (sum, item) => sum + item.placed,
      0
    );
    const avgCtc =
      totalStudentsPlaced > 0 ? totalCtcWeighted / totalStudentsPlaced : 0;

    // Calculate median CTC secured
    const securedCtcArray = [];
    placedWithCtc.forEach((item) => {
      for (let i = 0; i < item.placed; i++) {
        securedCtcArray.push(item.ctc);
      }
    });
    securedCtcArray.sort((a, b) => a - b);

    const midSecured = Math.floor(securedCtcArray.length / 2);
    const medianCtcSecured =
      securedCtcArray.length % 2 === 0
        ? (securedCtcArray[midSecured - 1] + securedCtcArray[midSecured]) / 2
        : securedCtcArray[midSecured];

    // Filter valid stipends
    const validStipends = placementData.filter((item) => item.stipend > 0);
    const stipendValues = validStipends
      .map((item) => item.stipend)
      .sort((a, b) => a - b);

    // Calculate average stipend
    const avgStipend =
      stipendValues.reduce((sum, val) => sum + val, 0) / stipendValues.length;

    // Calculate median stipend
    const midStipend = Math.floor(stipendValues.length / 2);
    const medianStipend =
      stipendValues.length % 2 === 0
        ? (stipendValues[midStipend - 1] + stipendValues[midStipend]) / 2
        : stipendValues[midStipend];

    // Calculate FTE-only stats (exclude SLI-only offers)
    const fteOffers = placementData.filter(
      (item) => {
        if (item.ctc <= 0 || item.placed <= 0) return false;
        const type = item.type.toLowerCase();
        // Include FTE and SLI+FTE, exclude SLI-only
        return type.includes('fte');
      }
    );
    
    // Calculate average CTC for FTE offers (weighted)
    const totalFteCtcWeighted = fteOffers.reduce(
      (sum, item) => sum + item.ctc * item.placed,
      0
    );
    const totalFteStudentsPlaced = fteOffers.reduce(
      (sum, item) => sum + item.placed,
      0
    );
    const avgCtcFte =
      totalFteStudentsPlaced > 0 ? totalFteCtcWeighted / totalFteStudentsPlaced : 0;

    // Calculate median CTC for FTE offers
    const fteCtcArray = [];
    fteOffers.forEach((item) => {
      for (let i = 0; i < item.placed; i++) {
        fteCtcArray.push(item.ctc);
      }
    });
    fteCtcArray.sort((a, b) => a - b);

    const midFte = Math.floor(fteCtcArray.length / 2);
    const medianCtcFte =
      fteCtcArray.length > 0
        ? fteCtcArray.length % 2 === 0
          ? (fteCtcArray[midFte - 1] + fteCtcArray[midFte]) / 2
          : fteCtcArray[midFte]
        : 0;

    // Calculate placement stats
    const completedPlacements = placementData.filter(
      (item) => item.status === "Completed"
    );
    const totalPlaced = completedPlacements.reduce(
      (sum, item) => sum + item.placed,
      0
    );
    const placementPercentage = (((totalPlaced) / 490) * 100).toFixed(2);

    // Calculate total FTE placements
    const totalFtePlaced = fteOffers.reduce(
      (sum, item) => sum + item.placed,
      0
    );

    // Calculate FTE placement percentage
    const ftePlacementPercentage = ((totalFtePlaced / 490) * 100).toFixed(2);

    // Separate ON-CAMPUS and OFF-CAMPUS placements
    const isOffCampus = (company) => {
      const lowerCompany = company.toLowerCase();
      return lowerCompany.includes('(off)') || 
             lowerCompany.includes('(off-campus)') || 
             lowerCompany.includes('(off campus)');
    };

    const onCampusPlacements = placedWithCtc.filter(item => !isOffCampus(item.company));
    const offCampusPlacements = placedWithCtc.filter(item => isOffCampus(item.company));

    // ON-CAMPUS STATISTICS
    // On-Campus Total Students Placed
    const onCampusStudentsPlaced = onCampusPlacements.reduce((sum, item) => sum + item.placed, 0);
    const onCampusPlacementRate = ((onCampusStudentsPlaced / 490) * 100).toFixed(2);

    // On-Campus Average CTC - All types combined
    const onCampusTotalCtc = onCampusPlacements.reduce((sum, item) => sum + item.ctc * item.placed, 0);
    const avgOnCampusCtc = onCampusStudentsPlaced > 0 ? onCampusTotalCtc / onCampusStudentsPlaced : 0;

    // OFF-CAMPUS STATISTICS
    // Off-Campus Average CTC - All types combined
    const offCampusStudentsPlaced = offCampusPlacements.reduce((sum, item) => sum + item.placed, 0);
    const offCampusTotalCtc = offCampusPlacements.reduce((sum, item) => sum + item.ctc * item.placed, 0);
    const avgOffCampusCtc = offCampusStudentsPlaced > 0 ? offCampusTotalCtc / offCampusStudentsPlaced : 0;

    return {
      avgCtc: avgCtc.toFixed(2),
      medianCtcSecured: medianCtcSecured.toFixed(2),
      avgCtcFte: avgCtcFte.toFixed(2),
      medianCtcFte: medianCtcFte.toFixed(2),
      avgStipend: (avgStipend / 1000).toFixed(1),
      medianStipend: (medianStipend / 1000).toFixed(1),
      totalPlaced: totalPlaced,
      totalFtePlaced: totalFtePlaced,
      placementPercentage: parseFloat(placementPercentage),
      ftePlacementPercentage: parseFloat(ftePlacementPercentage),
      // On-Campus Stats
      onCampusStudentsPlaced: onCampusStudentsPlaced,
      onCampusPlacementRate: parseFloat(onCampusPlacementRate),
      avgOnCampusCtc: avgOnCampusCtc.toFixed(2),
      // Off-Campus Stats
      avgOffCampusCtc: avgOffCampusCtc.toFixed(2),
    };
  };

  // Wrapper functions for tracking
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (value.length > 2) {
      logSearch(value);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    logFilterChange('Status Filter', value);
  };

  const handleCampusFilterChange = (value) => {
    setCampusFilter(value);
    logFilterChange('Campus Filter', value);
  };

  const handleSortChange = (value) => {
    setSortType(value);
    logSortChange(value);
  };

  const handleStatsToggle = () => {
    setShowStats(!showStats);
    logStatsToggle(!showStats);
  };

  const filterAndSort = () => {
    let filtered = [...placementData];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    // Apply campus filter
    if (campusFilter !== 'all') {
      filtered = filtered.filter((item) => {
        const companyName = item.company.toLowerCase();
        const isOffCampus = companyName.includes('off') || companyName.includes('off-campus');
        
        if (campusFilter === 'off-campus') {
          return isOffCampus;
        } else if (campusFilter === 'on-campus') {
          return !isOffCampus;
        }
        return true;
      });
    }

    // Apply sorting
    switch (sortType) {
      case 'ctc-high':
        filtered.sort((a, b) => b.ctc - a.ctc);
        break;
      case 'ctc-low':
        filtered.sort((a, b) => a.ctc - b.ctc);
        break;
      case 'placed':
        filtered.sort((a, b) => b.placed - a.placed);
        break;
      case 'company':
        filtered.sort((a, b) => a.company.localeCompare(b.company));
        break;
      default:
        break;
    }

    setFilteredData(filtered);
  };

  return (
    <div className="App">
      <InfoBanner />
     
      <div className="container">
        <header>
          <h1>🎓 Placement Tracker 2025-26</h1>
          <p className="subtitle">LNMIIT Jaipur</p>
        </header>

        <ChangeLog editHistory={editHistory} />

        <button 
          className="stats-toggle-btn"
          onClick={handleStatsToggle}
        >
          <span>📊 Statistics</span>
          <svg 
            className={`dropdown-arrow ${showStats ? 'open' : ''}`}
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            fill="none"
          >
            <path 
              d="M4 6L8 10L12 6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showStats && (
        <div className="dashboard">
          <StatsCard label="Average CTC" value={`${stats.avgCtc} LPA`} />
          <StatsCard
            label="Median CTC "
            value={`${stats.medianCtcSecured} LPA`}
          />
          <StatsCard
            label="Average CTC (FTE)"
            value={`${stats.avgCtcFte} LPA`}
          />
          <StatsCard
            label="Median CTC (FTE)"
            value={`${stats.medianCtcFte} LPA`}
          />
          <StatsCard
            label="Avg On-Campus CTC"
            value={`${stats.avgOnCampusCtc} LPA`}
          />
          <StatsCard
            label="Avg Off-Campus CTC"
            value={`${stats.avgOffCampusCtc} LPA`}
          />
          <StatsCard
            label="Average Stipend"
            value={`₹${stats.avgStipend}K`}
          />
          <StatsCard
            label="Median Stipend"
            value={`₹${stats.medianStipend}K`}
          />
          <StatsCard
            label="Students Placed"
            value={`${stats.totalPlaced} / 490`}
          />
          <StatsCard
            label="FTE Offers"
            value={`${stats.totalFtePlaced} / 490`}
          />
          <StatsCard
            label="On-Campus Placed"
            value={`${stats.onCampusStudentsPlaced} / 490`}
          />
          
          <Odometer percentage={stats.placementPercentage} />
          <Odometer percentage={stats.ftePlacementPercentage || 0} label="FTE Rate" />
          <Odometer percentage={stats.onCampusPlacementRate} label="On-Campus Placement Rate" />
        </div>
        )}

        <FilterControls
          searchTerm={searchTerm}
          setSearchTerm={handleSearchChange}
          statusFilter={statusFilter}
          setStatusFilter={handleStatusFilterChange}
          campusFilter={campusFilter}
          setCampusFilter={handleCampusFilterChange}
          sortType={sortType}
          setSortType={handleSortChange}
        />

        <PlacementTable data={filteredData} />
      </div>

      <SuggestionBox />
    </div>
  );
}

export default App;
